import { useQuery, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useRef } from "react";
import { useAuthStore } from "../../../../console/store/useAuthStore";
import { enum_api_uri } from "../../utils/enum";
import useToastStore from "../../../../console/store/useToastStore";
import alertSound from "../../../../console/assets/sound/alarm.mp3";

interface Alert {
    alert_idx: number;
    idx: number;
    category: string;
    category_number: number;
    contents: string;
    u_read: boolean;
    reg_date: string;
}

interface AlertListResponse {
    unread_cnt: number;
    alert_list: Alert[];
}

const nativeKoreanTens: Record<number, string> = {
    10: "열",
    20: "스물", // ← '스무'에서 '스물'로 변경 (복합 수사 시)
    30: "서른",
    40: "마흔",
    50: "쉰",
    60: "예순",
    70: "일흔",
    80: "여든",
    90: "아흔",
};

const nativeKoreanOnes: Record<number, string> = {
    1: "한",
    2: "두",
    3: "세",
    4: "네",
    5: "다섯",
    6: "여섯",
    7: "일곱",
    8: "여덟",
    9: "아홉",
};

const formatOrderCount = (n: number): string => {
    if (n <= 0) return "0껀";
    if (n < 10) return `${nativeKoreanOnes[n]} 껀`;

    if (n < 100) {
        const tens = Math.floor(n / 10) * 10;
        const ones = n % 10;

        let tensWord = nativeKoreanTens[tens] || "";
        const onesWord = ones ? nativeKoreanOnes[ones] : "";

        // 🔍 20 단독일 때만 "스무" 사용, 복합 수에서는 "스물" 유지
        if (tens === 20 && ones === 0) tensWord = "스무";

        return `${tensWord}${onesWord} 껀`;
    }

    return `${n} 껀`;
};

const getKoreanVoice = async (): Promise<SpeechSynthesisVoice | null> => {
    if (!("speechSynthesis" in window)) return null;

    const selectKoreanVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null => {
        const koreans = voices.filter(v => v.lang.startsWith("ko"));
        if (koreans.length === 0) return null;
        return koreans.find(v => v.name.includes("Google")) || koreans[0];
    };

    // 이미 로드된 경우
    let voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) return selectKoreanVoice(voices);

    // 아직 로드 전 → 이벤트 감지 Promise
    return new Promise(resolve => {
        const handleVoicesChanged = () => {
            const voices = window.speechSynthesis.getVoices();
            window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
            resolve(selectKoreanVoice(voices));
        };

        window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
    });
};

export const useGetAlertList = (): UseQueryResult<AlertListResponse, Error> => {
    const accessToken = useAuthStore(state => state.accessToken);
    const { addToast } = useToastStore();

    const query = useQuery<AlertListResponse, Error>({
        queryKey: ["getAlertList"],
        queryFn: async () => {
            const response = await axios.get(enum_api_uri.get_alert_list, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            return response.data.data;
        },
        staleTime: 1000 * 60,
        refetchInterval: 1000 * 60, // ✅ 1분마다 자동 확인
    });

    // ✅ 기존 알람 효과음
    const playAlertSound = () => {
        const audio = new Audio(alertSound);
        audio.play().catch(() => console.warn("🔇 사용자 상호작용 전이라 소리 재생 불가"));
    };

    // ✅ 음성 알림
    const speakAlert = async (text: string): Promise<boolean> => {
        if (!("speechSynthesis" in window)) return false;

        try {
            const voice = await getKoreanVoice();
            if (!voice) return false;

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = "ko-KR";
            utterance.rate = 1;
            utterance.pitch = 1;
            utterance.volume = 1.5;
            utterance.voice = voice;

            return await new Promise<boolean>(resolve => {
                utterance.onstart = () => resolve(true);
                utterance.onerror = () => resolve(false);

                window.speechSynthesis.cancel(); // 기존 음성 중단
                window.speechSynthesis.speak(utterance);
            });
        } catch {
            return false;
        }
    };

    useEffect(() => {
        if (!query.data) return;

        const currentUnread = query.data.unread_cnt;
        // console.log("DEBUG - 현재 unread:", currentUnread, "dataUpdatedAt:", query.dataUpdatedAt);

        if (currentUnread > 0) {
            // console.log("🔥 알림 실행 미확인 주문건:", currentUnread);
            const speakMessage = `미확인 주문건이 ${formatOrderCount(currentUnread)} 있습니다.`;
            const toastMessage = `미확인 주문건이 ${currentUnread}건 있습니다.`;

            addToast({
                type: "notice",
                message: toastMessage,
            });

            // ✅ 음성 시도 → 실패 시 기본 알림음 재생
            speakAlert(speakMessage).then(success => {
                if (!success) playAlertSound();
            });
        }
    }, [query.dataUpdatedAt]);

    return query;
};
